import { useCallback } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type {
  BulkCreateTravelItemsRow,
  BulkResolveTravelItemsRowResult,
  BulkResolveTravelItemsRowStatus,
  TravelItemKind,
} from "@event-planner/shared";
import { requireAirportTimezone } from "@event-planner/shared";
import {
  travelSpecFor,
  type ImportSpec,
  type ParsedFlightImportRow,
  type ParsedLodgingImportRow,
  type ParsedTransportImportRow,
  type ParsedOtherTravelImportRow,
} from "@event-planner/shared/imports";
import { dateTimeLocalToTimestamp } from "../../../utils/timestamps";
import {
  ResolveAndImportDialog,
  type ProposalRow,
} from "../../../components/imports/ResolveAndImportDialog";
import { type BulkImportResultBucket } from "../../../components/BulkImportResultTable";
import {
  bulkCreateTravelItems,
  bulkResolveTravelItems,
} from "../../../services/travelItemService";

interface ExcelTravelImportDialogProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** IANA timezone — local-time strings get converted to UTC against this. */
  timezone: string;
  /** Which travel kind this dialog instance is importing. */
  kind: TravelItemKind;
  onImported?: () => void;
}

export type ParsedTravelRow =
  | ParsedFlightImportRow
  | ParsedLodgingImportRow
  | ParsedTransportImportRow
  | ParsedOtherTravelImportRow;

type TravelProposal = ProposalRow<
  ParsedTravelRow,
  BulkResolveTravelItemsRowResult
>;

type WriteResult = Awaited<ReturnType<typeof bulkCreateTravelItems>>;

const KIND_TITLE: Record<TravelItemKind, string> = {
  flight: "Flights",
  lodging: "Lodging",
  transport: "Transport",
  other: "Other Travel",
};

const KIND_NOUN: Record<TravelItemKind, { singular: string; plural: string }> = {
  flight: { singular: "Flight", plural: "Flights" },
  lodging: { singular: "Lodging", plural: "Lodging" },
  transport: { singular: "Transport", plural: "Transport" },
  other: { singular: "Item", plural: "Items" },
};

export function defaultIncludeForTravelStatus(
  status: BulkResolveTravelItemsRowStatus | "invalid",
): { include: boolean; locked: boolean } {
  switch (status) {
    case "new":
      return { include: true, locked: false };
    case "unknown_location":
    case "unknown_audience":
    case "unknown_session":
    case "failed":
    case "invalid":
      return { include: false, locked: true };
  }
}

/**
 * Bulk Travel import (one of flight / lodging / transport / other per
 * dialog instance). Wires `bulkResolveTravelItems` through the shared
 * `ResolveAndImportDialog` shell. Reviewing phase shows resolved
 * pickup/dropoff locations, audience target (with ADR-057 identity
 * info on email-as-audience matches), and optional sessionLink.
 *
 * Local-time -> UTC conversion happens admin-side via
 * `dateTimeLocalToTimestamp`. The same converted ISO is sent to both
 * resolve and write callables so any duplicate-detection that lands
 * later fires on the correct timestamps.
 */
export function ExcelTravelImportDialog({
  open,
  onClose,
  eventId,
  timezone,
  kind,
  onImported,
}: ExcelTravelImportDialogProps) {
  const spec = travelSpecFor(kind);

  // The per-kind spec narrows TParsed; the shell uses a union. The cast
  // relaxes the call-site type without affecting runtime behavior.
  const wideSpec = spec as unknown as ImportSpec<
    Record<string, string | undefined>,
    ParsedTravelRow
  >;

  const resolveRows = useCallback(
    async (rows: ParsedTravelRow[]) => {
      const callableRows = rows.map((r) => toCallableRow(r, timezone));
      const response = await bulkResolveTravelItems({
        eventId,
        rows: callableRows,
      });
      return response.results;
    },
    [eventId, timezone],
  );

  const writeRows = useCallback(
    async (rows: ParsedTravelRow[]) => {
      const callableRows = rows.map((r) => toCallableRow(r, timezone));
      return bulkCreateTravelItems({ eventId, rows: callableRows });
    },
    [eventId, timezone],
  );

  const renderProposalCard = useCallback(
    (proposal: TravelProposal, onToggle: () => void) => (
      <ProposalCard proposal={proposal} onToggle={onToggle} />
    ),
    [],
  );

  return (
    <ResolveAndImportDialog<
      ParsedTravelRow,
      BulkResolveTravelItemsRowResult,
      WriteResult
    >
      open={open}
      onClose={onClose}
      onImported={onImported}
      title={`Import ${KIND_TITLE[kind]} from Excel`}
      entityNoun={KIND_NOUN[kind]}
      pickerHelpText={
        <>
          Upload an Excel file (.xlsx) for the <strong>{KIND_TITLE[kind]}</strong>{" "}
          sheet. Times are in event-local format (YYYY-MM-DD HH:MM, timezone:{" "}
          <strong>{timezone}</strong>). Locations, audiences, and the optional
          session link resolve server-side; you'll see the resolved values per
          row before anything is saved.
        </>
      }
      testIdSlug={`travel-${kind}`}
      spec={wideSpec}
      templateFileName={`travel-${kind}-import-template.xlsx`}
      sheetName={KIND_TITLE[kind]}
      resolveRows={resolveRows}
      writeRows={writeRows}
      defaultIncludeForResolution={(resolution) =>
        defaultIncludeForTravelStatus(resolution?.status ?? "invalid")
      }
      renderProposalCard={renderProposalCard}
      buildResultBuckets={({ writeResult, includedProposals, skippedProposals }) =>
        buildTravelBuckets(writeResult, includedProposals, skippedProposals)
      }
      importedAnythingFromResult={(result) => result.summary.created > 0}
    />
  );
}

// ---------------------------------------------------------------------------
// Per-row proposal card (travel-specific layout)
// ---------------------------------------------------------------------------

function ProposalCard({
  proposal,
  onToggle,
}: {
  proposal: TravelProposal;
  onToggle: () => void;
}) {
  const { resolution, parsed, include, includeLocked, lineNumber } = proposal;

  if (!resolution) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 1.5 }}
        data-testid={`proposal-row-${lineNumber}`}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Checkbox
            checked={false}
            disabled
            data-testid={`proposal-include-${lineNumber}`}
          />
          <Stack spacing={0.5} flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={500}>
              Row {lineNumber}: {rowLabel(parsed.data)}
            </Typography>
            <Typography variant="caption" color="error.main">
              {parsed.message ?? "Format error"}
            </Typography>
          </Stack>
          <Chip size="small" label="Format error" color="error" />
        </Stack>
      </Paper>
    );
  }

  const audienceProposal = resolution.proposal?.audience;
  const audienceText: { label: string; tooltipParts: string[]; isPersonal: boolean; photoURL: string | null } | null = (() => {
    if (!audienceProposal) return null;
    if (audienceProposal.source === "named") {
      return {
        label: audienceProposal.name,
        tooltipParts: [`Group: ${audienceProposal.name}`],
        isPersonal: false,
        photoURL: null,
      };
    }
    const tooltipParts = [
      audienceProposal.displayName,
      audienceProposal.pending
        ? "Pre-claim contact (single-person target)"
        : "Active member (single-person target)",
    ];
    if (audienceProposal.priorEventNames.length > 0) {
      tooltipParts.push(
        `Prior events: ${audienceProposal.priorEventNames.join(", ")}`,
      );
    }
    return {
      label: audienceProposal.displayName,
      tooltipParts,
      isPersonal: true,
      photoURL: audienceProposal.photoURL,
    };
  })();

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, opacity: includeLocked ? 0.85 : 1 }}
      data-testid={`proposal-row-${lineNumber}`}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Checkbox
          checked={include}
          disabled={includeLocked}
          onChange={onToggle}
          data-testid={`proposal-include-${lineNumber}`}
        />
        <Stack spacing={0.75} flex={1} minWidth={0}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" fontWeight={500}>
              Row {lineNumber}: {resolution.label}
            </Typography>
            <StatusChip status={resolution.status} />
          </Stack>

          <Stack spacing={0.25}>
            {resolution.proposal?.pickupLocation && (
              <Typography variant="caption" color="text.secondary">
                Pickup: {resolution.proposal.pickupLocation.label}
              </Typography>
            )}
            {resolution.proposal?.dropoffLocation && (
              <Typography variant="caption" color="text.secondary">
                {resolution.kind === "lodging" ? "Hotel" : "Drop-off"}:{" "}
                {resolution.proposal.dropoffLocation.label}
              </Typography>
            )}
            {resolution.proposal?.sessionLink && (
              <Typography variant="caption" color="text.secondary">
                Session: {resolution.proposal.sessionLink.title}
              </Typography>
            )}
          </Stack>

          {audienceText && (
            <>
              <Divider />
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Audience:
                </Typography>
                <Tooltip
                  title={
                    <Box sx={{ whiteSpace: "pre-line" }}>
                      {audienceText.tooltipParts.join("\n")}
                    </Box>
                  }
                >
                  <Chip
                    size="small"
                    avatar={
                      audienceText.isPersonal && audienceText.photoURL ? (
                        <Avatar src={audienceText.photoURL} />
                      ) : undefined
                    }
                    label={audienceText.label}
                    color={audienceText.isPersonal ? "success" : "info"}
                  />
                </Tooltip>
              </Stack>
            </>
          )}

          {resolution.message && resolution.status !== "new" && (
            <Typography variant="caption" color="warning.main">
              {resolution.message}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

function StatusChip({ status }: { status: BulkResolveTravelItemsRowStatus }) {
  switch (status) {
    case "new":
      return <Chip size="small" label="New, ready" color="success" />;
    case "unknown_location":
      return <Chip size="small" label="Unknown location" color="warning" />;
    case "unknown_audience":
      return <Chip size="small" label="Unknown audience" color="warning" />;
    case "unknown_session":
      return <Chip size="small" label="Unknown session" color="warning" />;
    case "failed":
      return <Chip size="small" label="Failed" color="error" />;
  }
}

// ---------------------------------------------------------------------------
// Done-phase result bucketing
// ---------------------------------------------------------------------------

interface ResultRow {
  lineNumber: number;
  label: string;
  status: "success" | "ref_issue" | "skipped" | "failed" | "invalid";
  message: string | null;
}

function buildTravelBuckets(
  writeResult: WriteResult | null,
  includedProposals: TravelProposal[],
  skippedProposals: TravelProposal[],
): BulkImportResultBucket[] {
  const writeRows: ResultRow[] = includedProposals.map((p, i) => {
    const r = writeResult?.results[i];
    if (!r) {
      return {
        lineNumber: p.lineNumber,
        label: rowLabel(p.parsed.data),
        status: "failed" as const,
        message: "Skipped at write phase",
      };
    }
    const status: ResultRow["status"] =
      r.status === "created"
        ? "success"
        : r.status === "unknown_location" ||
            r.status === "unknown_audience" ||
            r.status === "unknown_session"
          ? "ref_issue"
          : "failed";
    return {
      lineNumber: p.lineNumber,
      label: r.label,
      status,
      message: r.message ?? null,
    };
  });

  const skipRows: ResultRow[] = skippedProposals.map((p) => {
    if (!p.resolution) {
      return {
        lineNumber: p.lineNumber,
        label: rowLabel(p.parsed.data),
        status: "invalid" as const,
        message: p.parsed.message ?? "Format error",
      };
    }
    return {
      lineNumber: p.lineNumber,
      label: p.resolution.label,
      status: "skipped" as const,
      message: p.resolution.message ?? null,
    };
  });

  const all = [...writeRows, ...skipRows].sort(
    (a, b) => a.lineNumber - b.lineNumber,
  );
  const success = all.filter((r) => r.status === "success");
  const refIssue = all.filter((r) => r.status === "ref_issue");
  const skipped = all.filter((r) => r.status === "skipped");
  const failed = all.filter(
    (r) => r.status === "failed" || r.status === "invalid",
  );

  return [
    {
      id: "success",
      label: "Successfully Added",
      color: "success",
      rows: success.map((r) => ({
        id: `s-${r.lineNumber}`,
        primary: r.label,
        secondary: `Row ${r.lineNumber}`,
      })),
    },
    {
      id: "ref-issues",
      label: "Reference Issues",
      color: "warning",
      description:
        "Locations, audiences, or sessions that don't exist in this event yet. Add them via the admin pages, then re-import.",
      rows: refIssue.map((r) => ({
        id: `r-${r.lineNumber}`,
        primary: r.label,
        secondary: `Row ${r.lineNumber}: ${r.message ?? "unresolved"}`,
      })),
    },
    {
      id: "skipped",
      label: "Skipped",
      color: "default",
      description:
        "Rows the organizer chose not to import (locked off in the review phase).",
      rows: skipped.map((r) => ({
        id: `k-${r.lineNumber}`,
        primary: r.label,
        secondary: `Row ${r.lineNumber}${r.message ? `: ${r.message}` : ""}`,
      })),
    },
    {
      id: "failed",
      label: "Failed",
      color: "error",
      description: "Validation errors or callable rejections.",
      rows: failed.map((r) => ({
        id: `f-${r.lineNumber}`,
        primary: r.label,
        secondary: `Row ${r.lineNumber}: ${r.message ?? "unknown error"}`,
      })),
    },
  ];
}

// ---------------------------------------------------------------------------
// Per-kind helpers
// ---------------------------------------------------------------------------

function rowLabel(data: ParsedTravelRow): string {
  switch (data.kind) {
    case "flight":
      return (
        [data.airline, data.flightNumber].filter(Boolean).join(" ") || "Flight"
      );
    case "lodging":
      return data.locationLabel ?? "Lodging";
    case "transport":
      return data.mode;
    case "other":
      return data.title || "(no title)";
  }
}

function toCallableRow(
  data: ParsedTravelRow,
  timezone: string,
): BulkCreateTravelItemsRow {
  const toIso = (local: string | null, localTimezone = timezone): string | null =>
    local
      ? dateTimeLocalToTimestamp(local, localTimezone).toDate().toISOString()
      : null;
  const flightIso = (
    local: string | null,
    airportCode: string | null,
    fieldLabel: string,
  ): string | null =>
    local
      ? dateTimeLocalToTimestamp(
          local,
          requireAirportTimezone(airportCode, fieldLabel),
        )
          .toDate()
          .toISOString()
      : null;
  switch (data.kind) {
    case "flight":
      return {
        kind: "flight",
        audience: data.audience,
        reminder: data.reminder,
        sessionLinkTitle: null,
        direction: data.direction,
        airline: data.airline,
        flightNumber: data.flightNumber,
        departureAirport: data.departureAirport,
        departureTerminal: data.departureTerminal,
        departureAt: flightIso(
          data.departureAtLocal,
          data.departureAirport,
          "departure",
        ),
        arrivalAirport: data.arrivalAirport,
        arrivalTerminal: data.arrivalTerminal,
        arrivalAt: flightIso(data.arrivalAtLocal, data.arrivalAirport, "arrival"),
        confirmationCode: data.confirmationCode,
        notes: data.notes,
      };
    case "lodging":
      return {
        kind: "lodging",
        audience: data.audience,
        reminder: data.reminder,
        sessionLinkTitle: null,
        locationLabel: data.locationLabel,
        checkInAt: toIso(data.checkInAtLocal),
        checkOutAt: toIso(data.checkOutAtLocal),
        confirmationCode: data.confirmationCode,
        notes: data.notes,
      };
    case "transport":
      return {
        kind: "transport",
        audience: data.audience,
        reminder: data.reminder,
        sessionLinkTitle: data.sessionLinkTitle,
        mode: data.mode,
        pickupLocationLabel: data.pickupLocationLabel,
        dropoffLocationLabel: data.dropoffLocationLabel,
        pickupAt: toIso(data.pickupAtLocal),
        code: data.code,
        voucherUrl: data.voucherUrl,
        spendLimit: data.spendLimit,
        validityFrom: toIso(data.validityFromLocal),
        validityUntil: toIso(data.validityUntilLocal),
        notes: data.notes,
      };
    case "other":
      return {
        kind: "other",
        audience: data.audience,
        reminder: data.reminder,
        sessionLinkTitle: null,
        title: data.title,
        description: data.description,
        time: toIso(data.timeLocal),
        notes: data.notes,
      };
  }
}
